// TransactionOffersPanel - KOPS-711
// Multiple offers per deal. Renders in the Documents tab of TransactionDetails.
// Each offer is a collapsible block with its own documents (offer_id-tagged uploads),
// status lifecycle, and counter history. Parent deal stage is derived server-side:
// accepting an offer advances to under_contract; a fallen-through accepted offer reverts.

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Card, Button, Modal, Form, Badge, Spinner, Row, Col, Alert } from 'react-bootstrap';
import {
  FaPlus, FaFileContract, FaChevronDown, FaChevronRight, FaUpload,
  FaCheck, FaTimes, FaReply, FaFileAlt, FaUserTie
} from 'react-icons/fa';
import axiosInstance from '../../helpers/api';
import { toast } from 'react-toastify';
import OfferIntakeLinkButton from './OfferIntakeLinkButton';

interface Offer {
  id: string;
  transaction_id: string;
  offer_number: number;
  buyer_name?: string;
  buyer_agent?: { name?: string; email?: string; phone?: string; brokerage?: string } | null;
  seller_name?: string | null;
  listing_agent?: { name?: string; email?: string; phone?: string; brokerage?: string } | null;
  offer_price?: number | null;
  earnest_money?: number | null;
  financing_type?: string | null;
  concessions_requested?: number | null;
  buyer_broker_comp?: string | null;
  coe_date?: string | null;
  expiration?: string | null;
  status: string;
  counter_history?: any[] | null;
  received_at: string;
  responded_at?: string | null;
  notes?: string | null;
  document_count?: number;
}

interface OfferDocument {
  document_id: string;
  document_role?: string;
  status?: string;
  added_at?: string;
  filename?: string;
  original_filename?: string;
  document_type?: string;
  s3_url?: string;
  processing_status?: string;
  ai_summary?: string | null;
  ai_document_type?: string | null;
}

// KOPS-711: required document slots per offer.
// NOT hardcoded — pulled from the brokerage's own checklist templates
// (/broker-checklist/:brokerageId/documents). An incoming offer carries the
// buyer-side purchase package, so we query transaction_type=purchase,
// agent_role=buyer_agent and use the brokerage's required classification.
interface OfferDocSlot {
  key: string; // brokerage checklist document name; sent as checklist_name on upload
  label: string;
  required: boolean;
}

interface TransactionOffersPanelProps {
  transactionId: string;
  brokerageId: string;
  transactionType?: string; // 'listing' | 'purchase' | ...
  clientName?: string; // deal client — on purchase deals this IS the buyer
  onOffersChanged?: () => void; // parent refresh (stage may have changed)
}

const STATUS_BADGES: Record<string, string> = {
  received: 'info',
  reviewing: 'primary',
  countered: 'warning',
  accepted: 'success',
  rejected: 'secondary',
  expired: 'secondary',
  withdrawn: 'secondary',
  cancelled: 'danger'
};

const ACTIVE_STATUSES = ['received', 'reviewing', 'countered'];

const fmtMoney = (v?: number | null) =>
  v == null ? '—' : `$${Number(v).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;

const fmtDate = (v?: string | null) => (v ? new Date(v).toLocaleDateString() : '—');

const EMPTY_FORM = {
  buyer_name: '',
  agent_name: '',
  agent_email: '',
  agent_phone: '',
  agent_brokerage: '',
  seller_name: '',
  la_name: '',
  la_email: '',
  la_phone: '',
  la_brokerage: '',
  offer_price: '',
  earnest_money: '',
  financing_type: '',
  coe_date: '',
  expiration: '',
  notes: ''
};

const TransactionOffersPanel: React.FC<TransactionOffersPanelProps> = ({
  transactionId,
  brokerageId,
  transactionType,
  clientName,
  onOffersChanged
}) => {
  // On a purchase deal WE are the buyer's side: buyer is known (the client),
  // the unknown side is the listing agent + seller.
  const isBuyerSide = transactionType === 'purchase';
  const [offers, setOffers] = useState<Offer[]>([]);
  const [loading, setLoading] = useState(true);
  const [docSlots, setDocSlots] = useState<OfferDocSlot[]>([]);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ ...EMPTY_FORM });
  const [expandedOfferId, setExpandedOfferId] = useState<string | null>(null);
  const [offerDocuments, setOfferDocuments] = useState<Record<string, OfferDocument[]>>({});
  const [offerEmails, setOfferEmails] = useState<Record<string, any[]>>({});
  const [uploadingOfferId, setUploadingOfferId] = useState<string | null>(null);
  const [actionBusy, setActionBusy] = useState<string | null>(null);
  const [counterOffer, setCounterOffer] = useState<Offer | null>(null);
  const [editOffer, setEditOffer] = useState<Offer | null>(null);
  const [editForm, setEditForm] = useState<any>({});
  const [savingEdit, setSavingEdit] = useState(false);
  const [counterNote, setCounterNote] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);
  const uploadTargetOffer = useRef<string | null>(null);
  const uploadTargetType = useRef<{ type: string; label: string } | null>(null);

  const fetchOffers = useCallback(async () => {
    try {
      const res = await axiosInstance.get(`/transaction-offers/transaction/${transactionId}`);
      if (res.data?.success) {
        setOffers(res.data.offers || []);
      }
    } catch (err) {
      console.error('KOPS-711: failed to load offers', err);
    } finally {
      setLoading(false);
    }
  }, [transactionId]);

  useEffect(() => {
    fetchOffers();
  }, [fetchOffers]);

  // Brokerage-defined required documents for an incoming offer (buyer-side purchase package)
  useEffect(() => {
    if (!brokerageId) return;
    (async () => {
      try {
        const res = await axiosInstance.get(`/broker-checklist/${brokerageId}/documents`, {
          params: { transaction_type: 'purchase', agent_role: 'buyer_agent' }
        });
        if (res.data?.success) {
          const docs = res.data.documents || [];
          const isReq = (d: any) => d?.isRequired === true || d?.category === 'required';
          const slots: OfferDocSlot[] = docs
            .filter((d: any) => d?.name)
            .map((d: any) => ({ key: d.name, label: d.name, required: isReq(d) }))
            // required first, then conditional/optional
            .sort((a: OfferDocSlot, b: OfferDocSlot) => Number(b.required) - Number(a.required));
          setDocSlots(slots);
        }
      } catch (err) {
        console.error('KOPS-711: failed to load brokerage offer checklist', err);
      }
    })();
  }, [brokerageId]);

  const fetchOfferDocuments = async (offerId: string) => {
    try {
      const res = await axiosInstance.get(`/transaction-offers/${offerId}`);
      if (res.data?.success) {
        setOfferDocuments((prev) => ({ ...prev, [offerId]: res.data.documents || [] }));
        setOfferEmails((prev) => ({ ...prev, [offerId]: res.data.emails || [] }));
      }
    } catch (err) {
      console.error('KOPS-711: failed to load offer documents', err);
    }
  };

  const toggleExpand = (offerId: string) => {
    const next = expandedOfferId === offerId ? null : offerId;
    setExpandedOfferId(next);
    if (next) fetchOfferDocuments(next);
  };

  const handleCreate = async () => {
    setCreating(true);
    try {
      const payload: any = {
        buyer_name: form.buyer_name || undefined,
        offer_price: form.offer_price ? Number(form.offer_price) : undefined,
        earnest_money: form.earnest_money ? Number(form.earnest_money) : undefined,
        financing_type: form.financing_type || undefined,
        coe_date: form.coe_date || undefined,
        expiration: form.expiration || undefined,
        notes: form.notes || undefined
      };
      if (form.agent_name || form.agent_email || form.agent_phone || form.agent_brokerage) {
        payload.buyer_agent = {
          name: form.agent_name || undefined,
          email: form.agent_email || undefined,
          phone: form.agent_phone || undefined,
          brokerage: form.agent_brokerage || undefined
        };
      }
      if (form.seller_name) payload.seller_name = form.seller_name;
      if (form.la_name || form.la_email || form.la_phone || form.la_brokerage) {
        payload.listing_agent = {
          name: form.la_name || undefined,
          email: form.la_email || undefined,
          phone: form.la_phone || undefined,
          brokerage: form.la_brokerage || undefined
        };
      }
      const res = await axiosInstance.post(`/transaction-offers/transaction/${transactionId}`, payload);
      if (res.data?.success) {
        toast.success(`Offer #${res.data.offer.offer_number} created — upload the offer documents to its section below`);
        setShowCreateModal(false);
        setForm({ ...EMPTY_FORM });
        await fetchOffers();
        setExpandedOfferId(res.data.offer.id);
        onOffersChanged?.();
      }
    } catch (err: any) {
      toast.error(err?.response?.data?.error || 'Failed to create offer');
    } finally {
      setCreating(false);
    }
  };

  const updateStatus = async (offer: Offer, status: string, extra: any = {}) => {
    setActionBusy(offer.id);
    try {
      const res = await axiosInstance.patch(`/transaction-offers/${offer.id}`, { status, ...extra });
      if (res.data?.success) {
        if (status === 'accepted') {
          toast.success(`Offer #${offer.offer_number} accepted — deal moved to Under Contract`);
        } else {
          toast.info(`Offer #${offer.offer_number} marked ${status}`);
        }
        await fetchOffers();
        onOffersChanged?.();
      }
    } catch (err: any) {
      toast.error(err?.response?.data?.error || 'Failed to update offer');
    } finally {
      setActionBusy(null);
    }
  };

  const openEdit = (offer: Offer) => {
    setEditOffer(offer);
    setEditForm({
      buyer_name: offer.buyer_name || '',
      offer_price: offer.offer_price ?? '',
      earnest_money: offer.earnest_money ?? '',
      financing_type: offer.financing_type || '',
      buyer_broker_comp: offer.buyer_broker_comp || '',
      coe_date: offer.coe_date ? String(offer.coe_date).substring(0, 10) : '',
      expiration: offer.expiration ? String(offer.expiration).substring(0, 10) : '',
      agent_name: offer.buyer_agent?.name || '',
      agent_email: offer.buyer_agent?.email || '',
      agent_phone: offer.buyer_agent?.phone || '',
      agent_brokerage: offer.buyer_agent?.brokerage || '',
      seller_name: offer.seller_name || '',
      la_name: offer.listing_agent?.name || '',
      la_email: offer.listing_agent?.email || '',
      la_phone: offer.listing_agent?.phone || '',
      la_brokerage: offer.listing_agent?.brokerage || '',
      notes: offer.notes || ''
    });
  };

  const handleEditSave = async () => {
    if (!editOffer) return;
    setSavingEdit(true);
    try {
      const f = editForm;
      const payload: any = {
        buyer_name: f.buyer_name || undefined,
        offer_price: f.offer_price !== '' ? Number(f.offer_price) : undefined,
        earnest_money: f.earnest_money !== '' ? Number(f.earnest_money) : undefined,
        financing_type: f.financing_type || undefined,
        buyer_broker_comp: f.buyer_broker_comp || undefined,
        coe_date: f.coe_date || undefined,
        expiration: f.expiration || undefined,
        notes: f.notes || undefined
      };
      if (f.agent_name || f.agent_email || f.agent_phone || f.agent_brokerage) {
        payload.buyer_agent = {
          name: f.agent_name || undefined,
          email: f.agent_email || undefined,
          phone: f.agent_phone || undefined,
          brokerage: f.agent_brokerage || undefined
        };
      }
      if (f.seller_name) payload.seller_name = f.seller_name;
      if (f.la_name || f.la_email || f.la_phone || f.la_brokerage) {
        payload.listing_agent = {
          name: f.la_name || undefined,
          email: f.la_email || undefined,
          phone: f.la_phone || undefined,
          brokerage: f.la_brokerage || undefined
        };
      }
      const res = await axiosInstance.patch(`/transaction-offers/${editOffer.id}`, payload);
      if (res.data?.success) {
        toast.success('Offer details updated');
        setEditOffer(null);
        await fetchOffers();
        if (expandedOfferId === editOffer.id) fetchOfferDocuments(editOffer.id);
        onOffersChanged?.();
      }
    } catch (err: any) {
      toast.error(err?.response?.data?.error || 'Failed to update offer');
    } finally {
      setSavingEdit(false);
    }
  };

  const handleCounterSubmit = async () => {
    if (!counterOffer) return;
    await updateStatus(counterOffer, 'countered', { counter_note: counterNote || undefined });
    setCounterOffer(null);
    setCounterNote('');
  };

  const startUpload = (offerId: string, docType?: string, docLabel?: string) => {
    uploadTargetOffer.current = offerId;
    uploadTargetType.current = docType ? { type: docType, label: docLabel || docType } : null;
    fileInputRef.current?.click();
  };

  const handleFileSelected = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const input = e.target;
    const file = input.files?.[0];
    const offerId = uploadTargetOffer.current;
    if (!file || !offerId) {
      input.value = '';
      return;
    }
    if (file.size === 0) {
      input.value = '';
      toast.error('That file is empty (0 bytes) — please re-select it.');
      return;
    }

    setUploadingOfferId(offerId);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('transaction_id', transactionId);
      formData.append('brokerage_id', brokerageId);
      formData.append('offer_id', offerId);
      const slot = uploadTargetType.current;
      formData.append('document_type', 'offer_document');
      if (slot) formData.append('checklist_name', slot.label);
      formData.append('description', slot ? `${slot.label} (offer document)` : `Offer document: ${file.name}`);
      const res = await axiosInstance.post('/transaction-documents/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      if (res.data?.success !== false) {
        toast.success(`${file.name} uploaded to offer`);
        await fetchOfferDocuments(offerId);
        await fetchOffers();
      }
    } catch (err: any) {
      toast.error(err?.response?.data?.error || 'Upload failed');
    } finally {
      setUploadingOfferId(null);
      uploadTargetOffer.current = null;
      uploadTargetType.current = null;
      // Clear AFTER the upload completes — clearing early can invalidate the
      // File reference mid-send and upload 0 bytes
      input.value = '';
    }
  };

  const activeCount = offers.filter((o) => ACTIVE_STATUSES.includes(o.status)).length;

  return (
    <Card className="mt-4 shadow-sm">
      <Card.Header className="d-flex justify-content-between align-items-center bg-white">
        <div className="d-flex align-items-center">
          <FaFileContract className="me-2 text-primary" />
          <strong>Offers</strong>
          {offers.length > 0 && (
            <Badge bg="primary" className="ms-2">
              {offers.length} total{activeCount > 0 ? ` • ${activeCount} active` : ''}
            </Badge>
          )}
        </div>
        <div className="d-flex gap-2">
          {/* Public intake link is for LISTINGS receiving offers — never buyer-side deals */}
          {transactionType === 'listing' && <OfferIntakeLinkButton transactionId={transactionId} />}
          <Button variant="primary" size="sm" onClick={() => {
            if (isBuyerSide && clientName) setForm((f) => ({ ...f, buyer_name: f.buyer_name || clientName }));
            setShowCreateModal(true);
          }}>
            <FaPlus className="me-1" /> Create New Offer
          </Button>
        </div>
      </Card.Header>
      <Card.Body>
        {loading ? (
          <div className="text-center py-3">
            <Spinner animation="border" size="sm" />
          </div>
        ) : offers.length === 0 ? (
          <Alert variant="light" className="mb-0 text-muted">
            No offers yet. When an offer comes in on this deal, click <strong>Create New Offer</strong> —
            all documents for that offer (contract, pre-qual, earnest money, counters) will live in its own section here.
          </Alert>
        ) : (
          offers.map((offer) => {
            const expanded = expandedOfferId === offer.id;
            const docs = offerDocuments[offer.id] || [];
            const isActive = ACTIVE_STATUSES.includes(offer.status);
            return (
              <Card key={offer.id} className={`mb-3 ${offer.status === 'accepted' ? 'border-success' : ''}`}>
                <Card.Header
                  className="d-flex justify-content-between align-items-center"
                  style={{ cursor: 'pointer', background: expanded ? '#f8f9fa' : '#fff' }}
                  onClick={() => toggleExpand(offer.id)}
                >
                  <div className="d-flex align-items-center flex-wrap gap-2">
                    {expanded ? <FaChevronDown className="me-2" /> : <FaChevronRight className="me-2" />}
                    <strong>Offer #{offer.offer_number}</strong>
                    <span className="text-muted">{offer.buyer_name || 'Unnamed buyer'}</span>
                    <strong>{fmtMoney(offer.offer_price)}</strong>
                    <Badge bg={STATUS_BADGES[offer.status] || 'secondary'} className="text-capitalize">
                      {offer.status}
                    </Badge>
                    {Number(offer.document_count) > 0 && (
                      <Badge bg="light" text="dark">
                        <FaFileAlt className="me-1" />
                        {offer.document_count} doc{Number(offer.document_count) === 1 ? '' : 's'}
                      </Badge>
                    )}
                  </div>
                  <small className="text-muted">Received {fmtDate(offer.received_at)}</small>
                </Card.Header>
                {expanded && (
                  <Card.Body>
                    <Row className="mb-3">
                      <Col md={3}>
                        <small className="text-muted d-block">Earnest Money</small>
                        {fmtMoney(offer.earnest_money)}
                      </Col>
                      <Col md={3}>
                        <small className="text-muted d-block">Financing</small>
                        <span className="text-capitalize">{offer.financing_type || '—'}</span>
                      </Col>
                      <Col md={3}>
                        <small className="text-muted d-block">Close of Escrow</small>
                        {fmtDate(offer.coe_date)}
                      </Col>
                      <Col md={3}>
                        <small className="text-muted d-block">Expires</small>
                        {fmtDate(offer.expiration)}
                      </Col>
                    </Row>
                    {offer.buyer_agent && (offer.buyer_agent.name || offer.buyer_agent.email) && (
                      <div className="mb-3">
                        <small className="text-muted d-block">
                          <FaUserTie className="me-1" /> Buyer's Agent
                        </small>
                        {offer.buyer_agent.name}
                        {offer.buyer_agent.brokerage ? ` — ${offer.buyer_agent.brokerage}` : ''}
                        {offer.buyer_agent.email ? ` • ${offer.buyer_agent.email}` : ''}
                        {offer.buyer_agent.phone ? ` • ${offer.buyer_agent.phone}` : ''}
                      </div>
                    )}
                    {(offer.seller_name || (offer.listing_agent && (offer.listing_agent.name || offer.listing_agent.brokerage))) && (
                      <div className="mb-3">
                        <small className="text-muted d-block">
                          <FaUserTie className="me-1" /> Listing Side
                        </small>
                        {offer.seller_name ? `Seller: ${offer.seller_name}` : ''}
                        {offer.seller_name && offer.listing_agent?.name ? ' • ' : ''}
                        {offer.listing_agent?.name || ''}
                        {offer.listing_agent?.brokerage ? ` — ${offer.listing_agent.brokerage}` : ''}
                        {offer.listing_agent?.email ? ` • ${offer.listing_agent.email}` : ''}
                        {offer.listing_agent?.phone ? ` • ${offer.listing_agent.phone}` : ''}
                      </div>
                    )}
                    {offer.notes && (
                      <div className="mb-3">
                        <small className="text-muted d-block">Notes</small>
                        {offer.notes}
                      </div>
                    )}
                    {Array.isArray(offer.counter_history) && offer.counter_history.length > 0 && (
                      <div className="mb-3">
                        <small className="text-muted d-block">Counter History</small>
                        {offer.counter_history.map((c: any, i: number) => (
                          <div key={i} className="small">
                            {new Date(c.at).toLocaleString()} — {c.note || 'Countered'}
                            {c.price_at_counter != null ? ` (${fmtMoney(c.price_at_counter)})` : ''}
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Offer documents — required checklist + extras */}
                    <div className="border rounded p-3 mb-3" style={{ background: '#f8f9fa' }}>
                      {(() => {
                        const slots = docSlots;
                        const slotKeys = slots.map((s) => s.key);
                        // Uploads store the brokerage checklist name as user_documents.filename
                        const docsForSlot = (key: string) => docs.filter((d) => d.filename === key);
                        const otherDocs = docs.filter((d) => !slotKeys.includes(d.filename || ''));
                        const requiredSlots = slots.filter((s) => s.required);
                        const filledRequired = requiredSlots.filter((s) => docsForSlot(s.key).length > 0).length;
                        return (
                          <>
                            <div className="d-flex justify-content-between align-items-center mb-2">
                              <strong className="small">Offer Documents</strong>
                              <Badge bg={filledRequired === requiredSlots.length ? 'success' : 'warning'} text={filledRequired === requiredSlots.length ? undefined : 'dark'}>
                                {filledRequired}/{requiredSlots.length} required submitted
                              </Badge>
                            </div>
                            {slots.map((slot) => {
                              const slotDocs = docsForSlot(slot.key);
                              const filled = slotDocs.length > 0;
                              return (
                                <div key={slot.key} className="d-flex align-items-center small py-2 border-bottom">
                                  {filled ? (
                                    <FaCheck className="me-2 text-success" />
                                  ) : (
                                    <FaFileAlt className={`me-2 ${slot.required ? 'text-danger' : 'text-secondary'}`} />
                                  )}
                                  <div className="me-2">
                                    <span className={filled ? '' : slot.required ? 'fw-semibold' : 'text-muted'}>
                                      {slot.label}
                                    </span>
                                    {slot.required && !filled && (
                                      <Badge bg="danger" className="ms-2">Required</Badge>
                                    )}
                                    {!slot.required && !filled && (
                                      <Badge bg="light" text="dark" className="ms-2">Optional</Badge>
                                    )}
                                    {slotDocs.map((d) => (
                                      <div key={d.document_id} className="text-muted">
                                        {d.s3_url ? (
                                          <a href={d.s3_url} target="_blank" rel="noreferrer">
                                            {d.original_filename || d.filename || 'View document'}
                                          </a>
                                        ) : (
                                          d.original_filename || d.filename || d.document_id
                                        )}
                                        {' '}• {d.status || 'pending'} • {fmtDate(d.added_at)}
                                        {d.ai_summary ? (
                                          <div className="fst-italic" style={{ fontSize: 12, color: '#6c757d' }}>
                                            🤖 {d.ai_summary}
                                          </div>
                                        ) : d.processing_status === 'pending' ? (
                                          <div className="fst-italic" style={{ fontSize: 12, color: '#adb5bd' }}>AI summary processing…</div>
                                        ) : null}
                                      </div>
                                    ))}
                                  </div>
                                  <Button
                                    variant={filled ? 'outline-secondary' : slot.required ? 'outline-danger' : 'outline-primary'}
                                    size="sm"
                                    className="ms-auto"
                                    disabled={uploadingOfferId === offer.id}
                                    onClick={() => startUpload(offer.id, slot.key, slot.label)}
                                  >
                                    {uploadingOfferId === offer.id ? (
                                      <Spinner animation="border" size="sm" />
                                    ) : (
                                      <>
                                        <FaUpload className="me-1" /> {filled ? 'Replace / Add' : 'Upload'}
                                      </>
                                    )}
                                  </Button>
                                </div>
                              );
                            })}
                            <div className="d-flex align-items-center pt-2">
                              <small className="text-muted me-2">Other documents</small>
                              <Button
                                variant="link"
                                size="sm"
                                className="p-0"
                                disabled={uploadingOfferId === offer.id}
                                onClick={() => startUpload(offer.id)}
                              >
                                <FaUpload className="me-1" /> Upload other
                              </Button>
                            </div>
                            {otherDocs.map((d) => (
                              <div key={d.document_id} className="small py-1">
                                <div className="d-flex align-items-center">
                                  <FaFileAlt className="me-2 text-secondary" />
                                  <span className="me-2">
                                    {d.s3_url ? (
                                      <a href={d.s3_url} target="_blank" rel="noreferrer">
                                        {d.original_filename || d.filename || d.document_id}
                                      </a>
                                    ) : (
                                      d.original_filename || d.filename || d.document_id
                                    )}
                                  </span>
                                  {d.ai_document_type && <Badge bg="info" className="me-1">{String(d.ai_document_type).replace(/_/g, ' ')}</Badge>}
                                  <Badge bg="light" text="dark">{d.status || 'pending'}</Badge>
                                  <span className="ms-auto text-muted">{fmtDate(d.added_at)}</span>
                                </div>
                                {d.ai_summary ? (
                                  <div className="fst-italic ms-4" style={{ fontSize: 12, color: '#6c757d' }}>🤖 {d.ai_summary}</div>
                                ) : d.processing_status === 'pending' ? (
                                  <div className="fst-italic ms-4" style={{ fontSize: 12, color: '#adb5bd' }}>AI summary processing…</div>
                                ) : null}
                              </div>
                            ))}
                          </>
                        );
                      })()}
                    </div>

                    {/* Email proof trail */}
                    {(offerEmails[offer.id] || []).length > 0 && (
                      <div className="border rounded p-3 mb-3" style={{ background: '#f8f9fa' }}>
                        <strong className="small d-block mb-1">Email Trail (proof of delivery)</strong>
                        {(offerEmails[offer.id] || []).map((em: any, i: number) => (
                          <div key={i} className="small text-muted py-1 border-bottom">
                            <span className="fw-semibold text-dark">{em.email_type || 'Email'}</span>
                            {' → '}{em.sent_to}
                            <span className="ms-2">{new Date(em.created_at).toLocaleString()}</span>
                            {em.ses_message_id && (
                              <span className="d-block" style={{ fontSize: 11, color: '#adb5bd' }}>
                                receipt id: {em.ses_message_id}
                              </span>
                            )}
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Lifecycle actions */}
                    <div className="d-flex flex-wrap gap-2">
                      <Button variant="outline-secondary" size="sm" onClick={() => openEdit(offer)}>
                        Edit Details
                      </Button>
                      {offer.status === 'received' && (
                        <Button variant="outline-primary" size="sm" disabled={actionBusy === offer.id}
                          onClick={() => updateStatus(offer, 'reviewing')}>
                          Start Reviewing
                        </Button>
                      )}
                      {isActive && (
                        <>
                          <Button variant="success" size="sm" disabled={actionBusy === offer.id}
                            onClick={() => {
                              if (window.confirm(`Accept Offer #${offer.offer_number} (${fmtMoney(offer.offer_price)})? The deal will move to Under Contract.`)) {
                                updateStatus(offer, 'accepted');
                              }
                            }}>
                            <FaCheck className="me-1" /> Accept
                          </Button>
                          <Button variant="warning" size="sm" disabled={actionBusy === offer.id}
                            onClick={() => { setCounterOffer(offer); setCounterNote(''); }}>
                            <FaReply className="me-1" /> Counter
                          </Button>
                          <Button variant="outline-danger" size="sm" disabled={actionBusy === offer.id}
                            onClick={() => updateStatus(offer, 'rejected')}>
                            <FaTimes className="me-1" /> Reject
                          </Button>
                        </>
                      )}
                      {offer.status === 'accepted' && (
                        <Button variant="outline-danger" size="sm" disabled={actionBusy === offer.id}
                          onClick={() => {
                            if (window.confirm(`Mark Offer #${offer.offer_number} as fell through? The deal will revert to active.`)) {
                              updateStatus(offer, 'cancelled');
                            }
                          }}>
                          <FaTimes className="me-1" /> Offer Fell Through
                        </Button>
                      )}
                    </div>
                  </Card.Body>
                )}
              </Card>
            );
          })
        )}
      </Card.Body>

      {/* Hidden file input for offer document uploads */}
      <input
        type="file"
        ref={fileInputRef}
        style={{ display: 'none' }}
        accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
        onChange={handleFileSelected}
      />

      {/* Create New Offer modal */}
      <Modal show={showCreateModal} onHide={() => setShowCreateModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            <FaFileContract className="me-2" />
            Create New Offer
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="text-muted small">
            Enter what you know — you can upload the offer PDF to this offer's document section after creating it,
            and edit terms any time.
          </p>
          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Buyer Name</Form.Label>
                <Form.Control value={form.buyer_name}
                  onChange={(e) => setForm({ ...form, buyer_name: e.target.value })}
                  placeholder="e.g. John & Jane Smith" />
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group className="mb-3">
                <Form.Label>Offer Price</Form.Label>
                <Form.Control type="number" min="0" value={form.offer_price}
                  onChange={(e) => setForm({ ...form, offer_price: e.target.value })}
                  placeholder="450000" />
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group className="mb-3">
                <Form.Label>Earnest Money</Form.Label>
                <Form.Control type="number" min="0" value={form.earnest_money}
                  onChange={(e) => setForm({ ...form, earnest_money: e.target.value })}
                  placeholder="5000" />
              </Form.Group>
            </Col>
          </Row>
          <Row>
            <Col md={4}>
              <Form.Group className="mb-3">
                <Form.Label>Financing</Form.Label>
                <Form.Select value={form.financing_type}
                  onChange={(e) => setForm({ ...form, financing_type: e.target.value })}>
                  <option value="">Select…</option>
                  <option value="conventional">Conventional</option>
                  <option value="fha">FHA</option>
                  <option value="va">VA</option>
                  <option value="cash">Cash</option>
                  <option value="other">Other</option>
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group className="mb-3">
                <Form.Label>Close of Escrow</Form.Label>
                <Form.Control type="date" value={form.coe_date}
                  onChange={(e) => setForm({ ...form, coe_date: e.target.value })} />
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group className="mb-3">
                <Form.Label>Offer Expires</Form.Label>
                <Form.Control type="date" value={form.expiration}
                  onChange={(e) => setForm({ ...form, expiration: e.target.value })} />
              </Form.Group>
            </Col>
          </Row>
          <hr />
          {isBuyerSide ? (
            <>
              <h6 className="mb-3"><FaUserTie className="me-2" />Listing Side (who you're submitting to)</h6>
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Seller Name(s)</Form.Label>
                    <Form.Control value={form.seller_name}
                      onChange={(e) => setForm({ ...form, seller_name: e.target.value })} />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Listing Agent Name</Form.Label>
                    <Form.Control value={form.la_name}
                      onChange={(e) => setForm({ ...form, la_name: e.target.value })} />
                  </Form.Group>
                </Col>
              </Row>
              <Row>
                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label>Listing Brokerage</Form.Label>
                    <Form.Control value={form.la_brokerage}
                      onChange={(e) => setForm({ ...form, la_brokerage: e.target.value })} />
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label>Listing Agent Email</Form.Label>
                    <Form.Control type="email" value={form.la_email}
                      onChange={(e) => setForm({ ...form, la_email: e.target.value })} />
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label>Listing Agent Phone</Form.Label>
                    <Form.Control value={form.la_phone}
                      onChange={(e) => setForm({ ...form, la_phone: e.target.value })} />
                  </Form.Group>
                </Col>
              </Row>
              <p className="text-muted small mb-0">
                Buyer and buyer's agent are pre-filled from this deal — the AI also reads both sides from the uploaded contract.
              </p>
            </>
          ) : (
            <>
              <h6 className="mb-3"><FaUserTie className="me-2" />Buyer's Agent</h6>
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Agent Name</Form.Label>
                    <Form.Control value={form.agent_name}
                      onChange={(e) => setForm({ ...form, agent_name: e.target.value })} />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Agent Brokerage</Form.Label>
                    <Form.Control value={form.agent_brokerage}
                      onChange={(e) => setForm({ ...form, agent_brokerage: e.target.value })} />
                  </Form.Group>
                </Col>
              </Row>
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Agent Email</Form.Label>
                    <Form.Control type="email" value={form.agent_email}
                      onChange={(e) => setForm({ ...form, agent_email: e.target.value })} />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Agent Phone</Form.Label>
                    <Form.Control value={form.agent_phone}
                      onChange={(e) => setForm({ ...form, agent_phone: e.target.value })} />
                  </Form.Group>
                </Col>
              </Row>
            </>
          )}
          <Form.Group>
            <Form.Label>Notes</Form.Label>
            <Form.Control as="textarea" rows={2} value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })} />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowCreateModal(false)}>Cancel</Button>
          <Button variant="primary" onClick={handleCreate} disabled={creating}>
            {creating ? <Spinner animation="border" size="sm" /> : <><FaPlus className="me-1" /> Create Offer</>}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Edit Details modal (listing agent) */}
      <Modal show={!!editOffer} onHide={() => setEditOffer(null)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Edit Offer #{editOffer?.offer_number} Details</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Buyer Name</Form.Label>
                <Form.Control value={editForm.buyer_name || ''}
                  onChange={(e) => setEditForm({ ...editForm, buyer_name: e.target.value })} />
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group className="mb-3">
                <Form.Label>Offer Price</Form.Label>
                <Form.Control type="number" min="0" value={editForm.offer_price}
                  onChange={(e) => setEditForm({ ...editForm, offer_price: e.target.value })} />
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group className="mb-3">
                <Form.Label>Earnest Money</Form.Label>
                <Form.Control type="number" min="0" value={editForm.earnest_money}
                  onChange={(e) => setEditForm({ ...editForm, earnest_money: e.target.value })} />
              </Form.Group>
            </Col>
          </Row>
          <Row>
            <Col md={3}>
              <Form.Group className="mb-3">
                <Form.Label>Financing</Form.Label>
                <Form.Select value={editForm.financing_type || ''}
                  onChange={(e) => setEditForm({ ...editForm, financing_type: e.target.value })}>
                  <option value="">Select…</option>
                  <option value="conventional">Conventional</option>
                  <option value="fha">FHA</option>
                  <option value="va">VA</option>
                  <option value="cash">Cash</option>
                  <option value="other">Other</option>
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group className="mb-3">
                <Form.Label>Buyer Broker Comp</Form.Label>
                <Form.Control placeholder="e.g. 2.5%" value={editForm.buyer_broker_comp || ''}
                  onChange={(e) => setEditForm({ ...editForm, buyer_broker_comp: e.target.value })} />
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group className="mb-3">
                <Form.Label>Close of Escrow</Form.Label>
                <Form.Control type="date" value={editForm.coe_date || ''}
                  onChange={(e) => setEditForm({ ...editForm, coe_date: e.target.value })} />
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group className="mb-3">
                <Form.Label>Expires</Form.Label>
                <Form.Control type="date" value={editForm.expiration || ''}
                  onChange={(e) => setEditForm({ ...editForm, expiration: e.target.value })} />
              </Form.Group>
            </Col>
          </Row>
          <hr />
          <h6 className="mb-3">Buyer's Agent</h6>
          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Name</Form.Label>
                <Form.Control value={editForm.agent_name || ''}
                  onChange={(e) => setEditForm({ ...editForm, agent_name: e.target.value })} />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Brokerage</Form.Label>
                <Form.Control value={editForm.agent_brokerage || ''}
                  onChange={(e) => setEditForm({ ...editForm, agent_brokerage: e.target.value })} />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Email</Form.Label>
                <Form.Control type="email" value={editForm.agent_email || ''}
                  onChange={(e) => setEditForm({ ...editForm, agent_email: e.target.value })} />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Phone</Form.Label>
                <Form.Control value={editForm.agent_phone || ''}
                  onChange={(e) => setEditForm({ ...editForm, agent_phone: e.target.value })} />
              </Form.Group>
            </Col>
          </Row>
          <hr />
          <h6 className="mb-3">Listing Side</h6>
          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Seller Name(s)</Form.Label>
                <Form.Control value={editForm.seller_name || ''}
                  onChange={(e) => setEditForm({ ...editForm, seller_name: e.target.value })} />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Listing Agent Name</Form.Label>
                <Form.Control value={editForm.la_name || ''}
                  onChange={(e) => setEditForm({ ...editForm, la_name: e.target.value })} />
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group className="mb-3">
                <Form.Label>Listing Brokerage</Form.Label>
                <Form.Control value={editForm.la_brokerage || ''}
                  onChange={(e) => setEditForm({ ...editForm, la_brokerage: e.target.value })} />
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group className="mb-3">
                <Form.Label>Listing Agent Email</Form.Label>
                <Form.Control type="email" value={editForm.la_email || ''}
                  onChange={(e) => setEditForm({ ...editForm, la_email: e.target.value })} />
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group className="mb-3">
                <Form.Label>Listing Agent Phone</Form.Label>
                <Form.Control value={editForm.la_phone || ''}
                  onChange={(e) => setEditForm({ ...editForm, la_phone: e.target.value })} />
              </Form.Group>
            </Col>
          </Row>
          <Form.Group>
            <Form.Label>Notes</Form.Label>
            <Form.Control as="textarea" rows={2} value={editForm.notes || ''}
              onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })} />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setEditOffer(null)}>Cancel</Button>
          <Button variant="primary" onClick={handleEditSave} disabled={savingEdit}>
            {savingEdit ? <Spinner animation="border" size="sm" /> : 'Save Changes'}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Counter modal */}
      <Modal show={!!counterOffer} onHide={() => setCounterOffer(null)}>
        <Modal.Header closeButton>
          <Modal.Title>Counter Offer #{counterOffer?.offer_number}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group>
            <Form.Label>Counter terms / note</Form.Label>
            <Form.Control as="textarea" rows={3} value={counterNote}
              onChange={(e) => setCounterNote(e.target.value)}
              placeholder="e.g. Countered at $465,000, COE 30 days, appliances excluded" />
          </Form.Group>
          <small className="text-muted">
            Upload the signed counter document to this offer's document section once it's out.
          </small>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setCounterOffer(null)}>Cancel</Button>
          <Button variant="warning" onClick={handleCounterSubmit}>
            <FaReply className="me-1" /> Mark Countered
          </Button>
        </Modal.Footer>
      </Modal>
    </Card>
  );
};

export default TransactionOffersPanel;
